import * as THREE from 'three';
import { mat, box } from './styles/blocky.js';
import type { StyleRenderer } from './style.js';

export const ROAD_WIDTH = 15;
export const LANES = 6;
const LANE_W = 2.3;

export interface Pose {
  position: THREE.Vector3;
  tangent: THREE.Vector3;
}

const UP = new THREE.Vector3(0, 1, 0);

export class Track {
  readonly curve: THREE.CatmullRomCurve3;
  readonly length: number;

  constructor() {
    const pts = [
      [0, 0],
      [70, -15],
      [125, -55],
      [135, -120],
      [95, -170],
      [30, -165],
      [-25, -120],
      [-80, -140],
      [-140, -100],
      [-135, -35],
      [-75, 5],
    ].map(([x, z]) => new THREE.Vector3(x, 0, z));
    this.curve = new THREE.CatmullRomCurve3(pts, true, 'catmullrom', 0.6);
    this.length = this.curve.getLength();
  }

  static laneOffset(lane: number): number {
    return (lane - (LANES - 1) / 2) * LANE_W;
  }

  /** World pose for a 0..1 progress in a given lane. Progress wraps. */
  pose(progress: number, lane: number, out?: Pose): Pose {
    const u = ((progress % 1) + 1) % 1;
    const o = out ?? { position: new THREE.Vector3(), tangent: new THREE.Vector3() };
    this.curve.getPointAt(u, o.position);
    this.curve.getTangentAt(u, o.tangent).normalize();
    const normal = new THREE.Vector3().crossVectors(UP, o.tangent).normalize();
    o.position.addScaledVector(normal, Track.laneOffset(lane));
    o.position.y = 0;
    return o;
  }

  /** Build road, markings and start line. */
  buildMesh(style: StyleRenderer): THREE.Group {
    const g = new THREE.Group();
    const N = 600;
    g.add(this.ribbon(N, -ROAD_WIDTH / 2, ROAD_WIDTH / 2, 0.02, style.roadMaterial()));
    const white = mat('#f5f5f5');
    g.add(this.ribbon(N, -ROAD_WIDTH / 2 + 0.1, -ROAD_WIDTH / 2 + 0.5, 0.03, white));
    g.add(this.ribbon(N, ROAD_WIDTH / 2 - 0.5, ROAD_WIDTH / 2 - 0.1, 0.03, white));
    // Centre dashes
    const dashes = Math.floor(this.length / 8);
    for (let i = 0; i < dashes; i++) {
      const u = i / dashes;
      const p = this.curve.getPointAt(u);
      const t = this.curve.getTangentAt(u);
      const m = box(0.3, 0.04, 3, '#fdd835', p.x, 0.035, p.z, 0);
      m.rotation.y = Math.atan2(t.x, t.z);
      m.castShadow = false;
      g.add(m);
    }
    // Start / finish line and gantry
    const p0 = this.curve.getPointAt(0);
    const t0 = this.curve.getTangentAt(0);
    const yaw = Math.atan2(t0.x, t0.z);
    const line = new THREE.Group();
    line.position.copy(p0);
    line.rotation.y = yaw;
    for (let i = 0; i < 12; i++) {
      for (let j = 0; j < 2; j++) {
        const x = -ROAD_WIDTH / 2 + 0.6 + i * (ROAD_WIDTH - 1.2) / 12;
        const sq = box((ROAD_WIDTH - 1.2) / 12, 0.04, 1.2, (i + j) % 2 ? '#f5f5f5' : '#263238', x + 0.6, 0.04, j * 1.2 - 0.6, 0);
        sq.castShadow = false;
        line.add(sq);
      }
    }
    line.add(box(0.6, 7, 0.6, '#f5f5f5', -ROAD_WIDTH / 2 - 1, 3.5, 0, 0.05));
    line.add(box(0.6, 7, 0.6, '#f5f5f5', ROAD_WIDTH / 2 + 1, 3.5, 0, 0.05));
    line.add(box(ROAD_WIDTH + 3, 1.2, 0.8, '#e53935', 0, 7.2, 0, 0.1));
    line.add(box(ROAD_WIDTH + 2.4, 0.6, 0.9, '#f5f5f5', 0, 7.2, 0, 0.05));
    // Grandstand
    const stand = new THREE.Group();
    stand.position.set(-ROAD_WIDTH / 2 - 8, 0, 0);
    for (let r = 0; r < 4; r++) {
      stand.add(box(2, 1 + r, 30, r % 2 ? '#1e88e5' : '#e53935', -r * 2, (1 + r) / 2, 0, 0.05));
      for (let s = -13; s <= 13; s += 2.2) {
        const seat = box(0.8, 0.8, 0.8, ['#fdd835', '#7cb342', '#fb8c00', '#8e24aa'][(r + Math.round(s)) & 3]!, -r * 2, 1 + r + 0.4, s, 0.12);
        stand.add(seat);
      }
    }
    line.add(stand);
    g.add(line);
    return g;
  }

  private ribbon(N: number, left: number, right: number, y: number, material: THREE.Material): THREE.Mesh {
    const pos = new Float32Array((N + 1) * 2 * 3);
    const uv = new Float32Array((N + 1) * 2 * 2);
    const idx: number[] = [];
    const p = new THREE.Vector3();
    const t = new THREE.Vector3();
    const n = new THREE.Vector3();
    for (let i = 0; i <= N; i++) {
      const u = (i % N) / N;
      this.curve.getPointAt(u, p);
      this.curve.getTangentAt(u, t);
      n.crossVectors(UP, t).normalize();
      const l = p.clone().addScaledVector(n, left);
      const r = p.clone().addScaledVector(n, right);
      pos.set([l.x, y, l.z, r.x, y, r.z], i * 6);
      uv.set([0, i / N, 1, i / N], i * 4);
      if (i < N) {
        const a = i * 2;
        idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    material.side = THREE.DoubleSide;
    const mesh = new THREE.Mesh(geo, material);
    mesh.receiveShadow = true;
    return mesh;
  }
}
